import { observer } from 'mobx-react';
import IconButton, { ICON_BUTTON_ICONS } from '../../../../../components/buttons/IconButton';

/**
 * ProfilerNameSearchInput - Reusable search input for name-based searches
 *
 * Generic search input component that works for any entity type (companies, individuals, etc.).
 * Supports keyboard navigation (ArrowDown/ArrowUp) to move focus to results.
 *
 * @param {string} searchKey - Current search value
 * @param {function} setSearchKey - Callback to update search value
 * @param {function} clearSearchKey - Callback to clear search
 * @param {boolean} disabled - Whether input is disabled
 * @param {string} placeholder - Input placeholder text
 * @param {string} label - Label text for the input
 * @param {string} inputId - Unique ID for the input element
 * @param {string} resultsContainerSelector - CSS selector for results container (for keyboard nav)
 */
const ProfilerNameSearchInput = observer(
  ({
    searchKey,
    setSearchKey,
    clearSearchKey,
    disabled = false,
    placeholder = 'Type name here',
    label = 'Name',
    inputId = 'ps-input-searchKey',
    resultsContainerSelector = '.profiler-name-search-results'
  }) => {
    return (
      <div className="search-key-input-wrapper">
        <div className="search-key-label">{label}</div>
        <div className="search-key-input-wrap">
          <input
            id={inputId}
            type="text"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            onKeyDown={(e) => {
              if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

              const container = document.querySelector(resultsContainerSelector);
              if (!container) return;

              const entries = container.querySelectorAll('.profiler-name-search-result-entry');
              const count = entries.length;
              if (count === 0) return;

              let target = null;
              if (count === 1) {
                target = entries[0];
              } else {
                target = e.key === 'ArrowDown' ? entries[0] : entries[count - 1];
              }

              if (target) {
                e.preventDefault();
                e.stopPropagation();
                target.focus();
              }
            }}
          />
          {!searchKey && <img className="search-icon" src={ICON_BUTTON_ICONS.SEARCH} />}
          {!!searchKey && (
            <IconButton
              id={`${inputId}-clear-icon`}
              icon={ICON_BUTTON_ICONS.X_LG}
              withFill={false}
              withBorder={false}
              onClick={clearSearchKey}
              disabled={disabled}
            />
          )}
        </div>
      </div>
    );
  }
);

export default ProfilerNameSearchInput;
